/* eslint-disable complexity -- 工作台壳层集中组合权限菜单、响应式侧栏和个人中心交互，各分支对应独立可见状态。 */
import * as AllIcon from '@ant-design/icons';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Empty,
  Flex,
  Layout,
  Menu,
  Popover,
  Result,
  Spin,
  Tag,
  Typography,
  type MenuProps,
} from '@yimoka/antd';
import { observer } from '@yimoka/react';
import { isSuccess } from '@yimoka/shared';
import {
  createElement,
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { DocumentTitle } from '@/components/document-title';
import { LanguageSwitcher } from '@/components/language-switcher';
import { GlobalLoading } from '@/components/loading';
import type { IAuthAccountMenuItem, IAuthAccountStaff } from '@/entities/auth-account.d';
import { useTranslation } from '@/locales/i18n';
import { getCurrentLanguage, subscribeLanguage } from '@/locales/language';
import { createRemoteComponent } from '@/mf/load-remote';
import type { InteractionAccess } from '@/modules/workspace/interaction-access';
import {
  confirmWorkspaceNavigation,
  hasWorkspaceNavigationGuard,
  installWorkspacePopstateGuard,
} from '@/modules/workspace/navigation-guard';
import type { IWorkspaceRemoteProps } from '@/modules/workspace/remote-props';
import { useRealmBrand } from '@/realm-profile/profile';
import { clearAuthSession, getStaff } from '@/session/auth-session';
import { TENANT_SELECTOR_SWITCH_PATH } from '@/utils/auth-redirect';

import { workspaceBiz } from './biz';
import {
  hasPersonalMenuIcon,
  hasWorkspaceMenuIcon,
  PersonalMenuIcon,
  WorkspaceMenuIcon,
} from './icons';
import { useWorkspaceStyles } from './index.style';
import {
  findFirstWorkspaceMenuPath,
  findWorkspaceMenuTrail,
  isClassSpaceWorkspacePath,
  isCreatorCenterRemotePath,
  isHrWorkspacePath,
  isInteractionWorkspacePath,
  isTeacherStudioWorkspacePath,
  isWorkspaceAccountPath,
  isWorkspaceArtifactPath,
  isWorkspaceELearningPath,
  isWorkspaceNoticePath,
  isWorkspaceRemotePage,
  normalizeWorkspaceMenus,
  resolveInteractionAccess,
  toTenantRouterPath,
  type WorkspaceMenuItem,
} from './menu';
import {
  useWorkspaceIMSnapshot,
  useWorkspaceOpenIMBridge,
} from './openim-boundary';
import {
  getPersonalMenuTitle,
  isPersonalMenuRemotePage,
  normalizePersonalMenus,
  resolvePersonalMenuAction,
} from './personal-menu';

const SIDER_WIDTH = 188;
const COLLAPSED_WIDTH = 64;
const DEFAULT_AVATAR_URL = '/assets/default-avatar.png';
const TenantWorkspaceRemote = createRemoteComponent<IWorkspaceRemoteProps>('tenant-web-workspace/App');
const TenantHrWorkspaceRemote = createRemoteComponent<IWorkspaceRemoteProps>('tenant-web-hr-workspace/App');

/** isImageIcon 判断菜单图标是否为可直接渲染的图片资源。 */
function isImageIcon(icon = '') {
  return /^(?:data:|https?:\/\/|\/)/.test(icon);
}

/** getRegisteredIcon 从 Ant Design 图标注册表中解析后端声明的组件名。 */
function getRegisteredIcon(icon?: string) {
  if (!icon) return null;
  const component = AllIcon[icon as keyof typeof AllIcon];
  return component ? createElement(component as ComponentType) : null;
}

/** getWorkspaceMenuIcon 兼容业务图标、图片资源与 Ant Design 图标。 */
function getWorkspaceMenuIcon(item: WorkspaceMenuItem) {
  if (!item.icon) return null;
  if (hasWorkspaceMenuIcon(item.icon)) {
    return <WorkspaceMenuIcon data-api-icon={item.icon} name={item.icon} />;
  }
  if (isImageIcon(item.icon)) {
    return <img
      alt=""
      data-api-icon={item.icon}
      height={22}
      src={item.icon}
      width={22}
    />;
  }
  return getRegisteredIcon(item.icon);
}

/** getPersonalMenuIcon 兼容个人菜单的业务图标、图片资源与 Ant Design 图标。 */
function getPersonalMenuIcon(item: IAuthAccountMenuItem) {
  if (!item.icon) return null;
  if (hasPersonalMenuIcon(item.icon)) {
    return <PersonalMenuIcon data-api-icon={item.icon} name={item.icon} />;
  }
  if (isImageIcon(item.icon)) {
    return <img
      alt=""
      data-api-icon={item.icon}
      height={24}
      src={item.icon}
      width={24}
    />;
  }
  return getRegisteredIcon(item.icon);
}

/** getWorkspaceMenuTarget 将父级菜单指向其首个可访问子页面。 */
function getWorkspaceMenuTarget(item: WorkspaceMenuItem) {
  return item.children?.length ? findFirstWorkspaceMenuPath(item.children) : (item.path ?? '');
}

/** toFirstLevelMenuItems 把权限菜单转换为侧栏一级导航配置。 */
function toFirstLevelMenuItems(items: WorkspaceMenuItem[]): MenuProps['items'] {
  return items.map((item) => {
    const target = getWorkspaceMenuTarget(item);
    return {
      icon: getWorkspaceMenuIcon(item),
      key: item.key,
      label: target ? <Link to={target}>{item.label}</Link> : item.label,
    };
  });
}

/** CollapseIcon 渲染工作台侧栏收起与展开图标。 */
function CollapseIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="14"
      viewBox="0 0 14 14"
      width="14"
    >
      <path d="M7.6836 7.00041L4.79621 9.88773L5.62116 10.7127L9.3335 7.00041L5.62116 3.28809L4.79621 4.11304L7.6836 7.00041Z" fill="currentColor" />
    </svg>
  );
}

/** MessageIcon 渲染工作台通知入口图标。 */
function MessageIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="16"
      viewBox="0 0 22 22"
      width="16"
    >
      <path d="M18.3333 15.5835H20.1666V17.4168H1.83325V15.5835H3.66659V9.16683C3.66659 5.11674 6.94983 1.8335 10.9999 1.8335C15.05 1.8335 18.3333 5.11674 18.3333 9.16683V15.5835ZM8.24992 19.2502H13.7499V21.0835H8.24992V19.2502Z" fill="currentColor" />
    </svg>
  );
}

/** MoreIcon 渲染个人中心菜单触发图标。 */
function MoreIcon() {
  return (
    <svg
      aria-hidden
      fill="currentColor"
      height="16"
      viewBox="0 0 16 16"
      width="16"
    >
      <circle cx="8" cy="5" r="1" />
      <circle cx="8" cy="8.5" r="1" />
      <circle cx="8" cy="12" r="1" />
    </svg>
  );
}

/** ArrowRightIcon 渲染个人菜单导航方向提示。 */
function ArrowRightIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="12"
      viewBox="0 0 14 14"
      width="12"
    >
      <path
        d="M5.25 2.625L9.625 7L5.25 11.375"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** WorkspaceContent 根据当前命中的菜单链路选择业务入口，兼容父级 /workspace 下挂载 AI 子菜单的权限数据。 */
function WorkspaceContent({
  imBridge,
  isRemotePage,
  interactionAccess,
  menus,
  remotesReady,
  tenantID,
}: {
  imBridge?: IWorkspaceRemoteProps['imBridge'];
  isRemotePage: boolean;
  interactionAccess: InteractionAccess;
  menus: WorkspaceMenuItem[];
  remotesReady: boolean;
  tenantID: string;
}) {
  const { styles } = useWorkspaceStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const isHrRemotePage = isHrWorkspacePath(location.pathname);
  const trail = useMemo(() => findWorkspaceMenuTrail(menus, location.pathname), [location.pathname, menus]);
  const rootMenu = trail[0] ?? menus[0];
  const currentMenu = trail[trail.length - 1] ?? rootMenu;
  const entries = rootMenu?.children ?? [];
  if (isRemotePage) {
    // 个人菜单可以单独授权审核入口，因此必须先挂载 remote，不能被空 workspace 菜单提前拦截。
    // 远端内部复用单例 Biz，租户变化时用 key 强制重挂载，避免沿用上一租户状态。
    return remotesReady
      ? (
        <Fragment key={tenantID}>
          {isHrRemotePage
            ? <TenantHrWorkspaceRemote menus={menus} />
            : <TenantWorkspaceRemote imBridge={imBridge} interactionAccess={interactionAccess} menus={menus} />}
        </Fragment>
      )
      : <GlobalLoading />;
  }
  if (!rootMenu) return <Empty description={t('Workspace:menuEmpty')} />;

  return (
    <Flex vertical gap="large">
      <Flex vertical gap={4}>
        <Typography.Title level={2} style={{ margin: 0 }}>{rootMenu.label}</Typography.Title>
        <Typography.Text type="secondary">{t('Workspace:menuSubtitle')}</Typography.Text>
      </Flex>
      {entries.length ? (
        <div className={styles.entryGrid} data-testid="workspace-entry-grid">
          {entries.map((item) => {
            const target = getWorkspaceMenuTarget(item);
            const card = (
              <Card
                key={item.key}
                className={styles.entryCard}
                data-testid={`workspace-entry-${item.key}`}
                extra={item === currentMenu ? <Tag color="processing">{t('Workspace:current')}</Tag> : null}
                title={item.label}
              >
                <Typography.Text type="secondary">{item.sourcePath || item.path}</Typography.Text>
              </Card>
            );
            return target ? <Link key={item.key} className={styles.entryCardLink} to={target}>{card}</Link> : card;
          })}
        </div>
      ) : (
        <Result
          icon={getWorkspaceMenuIcon(rootMenu)}
          subTitle={rootMenu.sourcePath || rootMenu.path}
          title={t('Workspace:entryReady', { name: rootMenu.label })}
        />
      )}
    </Flex>
  );
}

/** TenantWorkspacePage 负责租户工作台菜单壳层，并为 AI 与审核 remote 保留全屏布局。 */
export const TenantWorkspacePage = observer(({
  remotesReady = true,
  tenantID,
}: {
  remotesReady?: boolean;
  tenantID: string;
}) => {
  const { styles } = useWorkspaceStyles();
  const { t } = useTranslation();
  const language = useSyncExternalStore(subscribeLanguage, getCurrentLanguage, getCurrentLanguage);
  const location = useLocation();
  const navigate = useNavigate();
  const staff = getStaff<IAuthAccountStaff>();
  const imBridge = useWorkspaceOpenIMBridge(tenantID, staff?.id ?? '');
  const imSnapshot = useWorkspaceIMSnapshot(imBridge);
  const [collapsed, setCollapsed] = useState(false);
  const [personalMenuOpen, setPersonalMenuOpen] = useState(false);
  const stores = useMemo(() => workspaceBiz.getStores(tenantID), [tenantID]);
  const { personalMenuStore, tenantInfoStore, workspaceMenuStore } = stores;
  const workspaceMenus = useMemo(() => (
    isSuccess(workspaceMenuStore.response) ? normalizeWorkspaceMenus(workspaceMenuStore.response.data) : []
  ), [workspaceMenuStore.response]);
  const interactionAccess = useMemo(() => resolveInteractionAccess(workspaceMenus), [workspaceMenus]);
  const personalMenus = useMemo(() => (
    isSuccess(personalMenuStore.response) ? normalizePersonalMenus(personalMenuStore.response.data) : []
  ), [personalMenuStore.response]);
  const tenantInfo = isSuccess(tenantInfoStore.response) ? tenantInfoStore.response.data : undefined;
  const menuTrail = useMemo(
    () => findWorkspaceMenuTrail(workspaceMenus, location.pathname),
    [location.pathname, workspaceMenus],
  );
  const selectedKeys = menuTrail[0] ? [menuTrail[0].key] : [];
  // 名师工作室允许已知地址直接进入，但不补造 IAM 菜单；页面数据仍由 Accessible/Manageable 服务端权限收口。
  // 其他兼容深链也只挂载页面边界，不伪造菜单或数据权限。
  const isRemotePage = isWorkspaceRemotePage(menuTrail)
    || isHrWorkspacePath(location.pathname)
    || isInteractionWorkspacePath(location.pathname)
    || isClassSpaceWorkspacePath(location.pathname)
    || isCreatorCenterRemotePath(location.pathname)
    || isWorkspaceNoticePath(location.pathname)
    || isWorkspaceAccountPath(location.pathname)
    || isWorkspaceArtifactPath(location.pathname)
    || isTeacherStudioWorkspacePath(location.pathname)
    || isPersonalMenuRemotePage(personalMenus, location.pathname)
    || isWorkspaceELearningPath(location.pathname);
  const firstPath = findFirstWorkspaceMenuPath(workspaceMenus);
  // 资源空间统一展示员工 name；其他工作台页面继续保持既有实名展示，避免扩大本次产品语义。
  const staffName = staff?.name || t('Workspace:staffFallback');
  const tenantName = tenantInfo?.nameLocal || tenantInfo?.name
    || staff?.tenant?.nameLocal || staff?.tenant?.name || tenantID;
  const tenantLogo = tenantInfo?.logo || staff?.tenant?.logo || '/assets/logo.png';
  const brand = useRealmBrand(tenantName, tenantLogo);
  const unreadCount = imSnapshot?.totalUnread ?? 0;
  const noticeAriaLabel = unreadCount
    ? `${t('Workspace:notice')}，${unreadCount} 条未读`
    : t('Workspace:notice');

  useEffect(() => {
    if (tenantID) void workspaceBiz.mount(tenantID);
  }, [language, tenantID]);

  useLayoutEffect(() => installWorkspacePopstateGuard(), []);

  /** handleWorkspaceNavigationCapture 统一拦截宿主、个人菜单和入口卡片的站内 Link。 */
  const handleWorkspaceNavigationCapture = useCallback(async (event: MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || !hasWorkspaceNavigationGuard()
    ) return;
    const { target } = event;
    if (!(target instanceof Element)) return;
    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (!anchor || anchor.download || anchor.target === '_blank') return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    event.stopPropagation();
    if (!await confirmWorkspaceNavigation()) return;
    setPersonalMenuOpen(false);
    navigate(`${toTenantRouterPath(url.pathname, tenantID)}${url.search}${url.hash}`);
  }, [navigate, tenantID]);

  if ((workspaceMenuStore.loading || !workspaceMenuStore.response) && !isSuccess(workspaceMenuStore.response)) {
    return (
      <Flex
        vertical
        align="center"
        gap="middle"
        style={{ padding: 48 }}
      >
        <Spin />
        <Typography.Text type="secondary">{t('Workspace:menuLoading')}</Typography.Text>
      </Flex>
    );
  }

  if (!isSuccess(workspaceMenuStore.response)) {
    return (
      <Result
        extra={(
          <Button icon={<ReloadOutlined />} type="primary" onClick={() => void workspaceMenuStore.fetch()}>
            {t('Common:retry')}
          </Button>
        )}
        status="warning"
        subTitle={t('Workspace:menuErrorDesc')}
        title={t('Workspace:menuError')}
      />
    );
  }

  /** handlePersonalCommand 在切换租户或登出前等待当前业务页面完成保存。 */
  const handlePersonalCommand = async (item: IAuthAccountMenuItem) => {
    const action = resolvePersonalMenuAction(item);
    if (
      (action === 'switchTenant' || action === 'logout')
      && !await confirmWorkspaceNavigation()
    ) return;
    setPersonalMenuOpen(false);
    if (action === 'switchTenant') {
      // 显式携带主动切换语义，避免单租户被选择页的首登自动逻辑立即送回。
      window.location.assign(TENANT_SELECTOR_SWITCH_PATH);
      return;
    }
    if (action === 'logout') {
      // 登出清理用户态与租户员工态，避免返回登录页后被旧 token 自动送回租户。
      clearAuthSession();
      window.location.replace('/login');
      return;
    }
  };

  const personalMenuContent = (
    <div className={styles.personalMenu} data-testid="workspace-personal-menu-content">
      <LanguageSwitcher className={styles.personalLanguage} />
      {personalMenuStore.loading && !personalMenuStore.response ? (
        <div className={styles.personalMenuState}><Spin size="small" /></div>
      ) : null}
      {!personalMenuStore.loading && !personalMenus.length ? (
        <div className={styles.personalMenuState}>{t('Workspace:personalMenuEmpty')}</div>
      ) : null}
      {personalMenus.map((item, index) => {
        const key = `${item.path || getPersonalMenuTitle(item)}:${index}`;
        const action = resolvePersonalMenuAction(item);
        const icon = getPersonalMenuIcon(item);
        const content = <>
          {icon ? <span className={styles.personalMenuIcon}>{icon}</span> : null}
          <span className={styles.personalMenuTitle}>{getPersonalMenuTitle(item)}</span>
          <ArrowRightIcon />
        </>;
        return action === 'navigate' && item.path ? (
          <Link
            key={key}
            className={styles.personalMenuItem}
            to={item.path}
            onClick={() => setPersonalMenuOpen(false)}
          >
            {content}
          </Link>
        ) : (
          <button
            key={key}
            className={styles.personalMenuItem}
            role="menuitem"
            type="button"
            onClick={() => void handlePersonalCommand(item)}
          >
            {content}
          </button>
        );
      })}
    </div>
  );

  /** personalMenuPopover 复用桌面侧栏和移动端顶部的个人中心浮层交互。 */
  const personalMenuPopover = (trigger: ReactNode, placement: 'right' | 'topRight') => (
    <Popover
      arrow={false}
      content={personalMenuContent}
      open={personalMenuOpen}
      placement={placement}
      rootClassName={styles.menuPopover}
      trigger="click"
      onOpenChange={setPersonalMenuOpen}
    >
      {trigger}
    </Popover>
  );

  if (location.pathname === '/workspace' && firstPath) {
    // 认证恢复可能把用户带回含业务参数的工作台根地址；补齐首个菜单时必须保留查询与哈希。
    return (
      <Navigate
        replace
        to={{ pathname: firstPath, search: location.search, hash: location.hash }}
      />
    );
  }

  return (
    <Layout
      className={styles.layout}
      data-testid="tenant-workspace-layout"
      onClickCapture={event => void handleWorkspaceNavigationCapture(event)}
    >
      <DocumentTitle suffix={tenantName} title={t('Workspace:title')} />
      <Layout.Sider
        collapsible
        className={styles.sider}
        collapsed={collapsed}
        collapsedWidth={COLLAPSED_WIDTH}
        data-testid="tenant-workspace-sider"
        theme="light"
        trigger={null}
        width={SIDER_WIDTH}
      >
        <div className={styles.siderInner}>
          <div className={styles.brand}>
            <img alt={brand.logoAlt} className={styles.logo} src={brand.logoURL} />
            {!collapsed ? <span className={styles.brandTitle}>{brand.shortTitle}</span> : null}
          </div>
          <Menu
            className={styles.menu}
            data-testid="tenant-workspace-menu"
            inlineCollapsed={collapsed}
            items={toFirstLevelMenuItems(workspaceMenus)}
            mode="inline"
            selectedKeys={selectedKeys}
          />
          <button
            aria-label={collapsed ? t('Workspace:expand') : t('Workspace:collapse')}
            className={styles.collapseToggle}
            data-testid="workspace-collapse-toggle"
            type="button"
            onClick={() => setCollapsed(value => !value)}
          >
            <span className={`${styles.collapseIcon} ${collapsed ? '' : styles.collapseIconExpanded}`}>
              <CollapseIcon />
            </span>
          </button>
          {collapsed ? personalMenuPopover(
            <div className={styles.collapsedProfile}>
              <button
                className={styles.collapsedAvatarButton}
                data-testid="workspace-personal-menu"
                type="button"
              >
                <Avatar
                  className={styles.collapsedAvatar}
                  data-testid="workspace-profile-avatar"
                  src={staff?.avatar || DEFAULT_AVATAR_URL}
                >
                  {staffName.slice(0, 1)}
                </Avatar>
              </button>
            </div>,
            'right',
          ) : (
            <div className={styles.profile}>
              <div className={styles.profileContent}>
                <Avatar
                  className={styles.profileAvatar}
                  data-testid="workspace-profile-avatar"
                  src={staff?.avatar || DEFAULT_AVATAR_URL}
                >
                  {staffName.slice(0, 1)}
                </Avatar>
                <div className={styles.profileInfo}>
                  <div className={styles.profileName}>{staffName}</div>
                  <div className={styles.tenantName}>{tenantName}</div>
                </div>
              </div>
              <div className={styles.profileFooter}>
                {/* 旧工作台始终保留小铃铛；连接开关只控制 Runtime 和未读数，不能删除用户熟悉的入口。 */}
                <button
                  aria-label={noticeAriaLabel}
                  className={styles.footerButton}
                  data-testid="workspace-notice-entry"
                  type="button"
                  onClick={() => navigate('/workspace/notice')}
                >
                  <span className={styles.noticeIcon}>
                    <MessageIcon />
                    {/* 旧工作台只用红点提示“有未读”，具体数量进入通知页后再从会话列表查看。 */}
                    {unreadCount > 0 ? (
                      <span
                        aria-hidden="true"
                        className={styles.noticeDot}
                        data-testid="workspace-notice-dot"
                      />
                    ) : null}
                  </span>
                </button>
                {personalMenuPopover(
                  <button
                    aria-label={t('Workspace:personalMenu')}
                    className={styles.footerButton}
                    data-testid="workspace-personal-menu"
                    type="button"
                  >
                    <MoreIcon />
                  </button>,
                  'right',
                )}
              </div>
            </div>
          )}
        </div>
      </Layout.Sider>
      <Layout.Content className={`${styles.content} ${isRemotePage ? styles.workspaceContent : ''}`}>
        <main className={`${styles.contentPanel} ${isRemotePage ? styles.workspacePanel : ''}`}>
          <WorkspaceContent
            imBridge={imBridge}
            interactionAccess={interactionAccess}
            isRemotePage={isRemotePage}
            menus={workspaceMenus}
            remotesReady={remotesReady}
            tenantID={tenantID}
          />
        </main>
      </Layout.Content>
    </Layout>
  );
});

export default TenantWorkspacePage;
